import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpClient
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { backendEndPointToken } from '../app.config';

/**
 * TerminologyInterceptor
 * Redirects requests for FHIR ValueSets and CodeSystems from public servers 
 * (tx.fhir.org, server.fire.ly, hapi.fhir.org) to the local backend if they exist.
 */
export const terminologyInterceptor: HttpInterceptorFn = (req, next) => {
  const backendUrl = inject(backendEndPointToken);
  const http = inject(HttpClient);

  // 1. Detect public FHIR terminology requests
  const isPublicFHIR = /https?:\/\/(tx\.fhir\.org|server\.fire\.ly|hapi\.fhir\.org)/i.test(req.url);
  const isTerminology = req.url.includes('ValueSet') || req.url.includes('CodeSystem');

  if (req.method === 'GET' && isPublicFHIR && isTerminology) {
    const slugId = extractSlugId(req.url);
    if (slugId && slugId !== 'unknown') {
      const localUrl = `${backendUrl}/ValueSet/${slugId}`;
      
      // Optimistic Attempt: Try the local backend first.
      return next(req.clone({ url: localUrl })).pipe(
        catchError((err) => {
          // Fallback: If local fails (e.g. 404 not found), proceed with the original public URL.
          if (err.status === 404 || err.status === 0) {
            return next(req);
          }
          throw err;
        })
      );
    }
  }

  return next(req);
};

/**
 * Extracts a unique slug/id from a terminology URL.
 * Handles both ?url=... format and direct path segments.
 */
function extractSlugId(urlPath: string): string {
  try {
    const parsed = new URL(urlPath);
    // Case 1: url query parameter (canonical)
    const queryUrl = parsed.searchParams.get('url');
    if (queryUrl) {
      // Decode and pick last segment
      let slug = queryUrl.substring(queryUrl.lastIndexOf('/') + 1);
      if (slug.includes('?')) slug = slug.substring(0, slug.indexOf('?'));
      if (slug.includes('&')) slug = slug.substring(0, slug.indexOf('&'));
      return slug;
    }

    // Case 2: Direct path mapping (e.g. server.fire.ly/r4/ValueSet/appointmentstatus)
    const pathSegments = parsed.pathname.split('/').filter(s => !!s);
    if (pathSegments.length > 0) {
      // The slug is usually the last segment
      let slug = pathSegments[pathSegments.length - 1];
      // If last segment is $expand, the slug is the one before it
      if (slug === '$expand' && pathSegments.length > 1) {
        slug = pathSegments[pathSegments.length - 2];
      }
      return slug;
    }
  } catch (err) {
    // Fallback for non-absolute URLs or parsing errors
    const lastPart = urlPath.substring(urlPath.lastIndexOf('/') + 1);
    const queryIdx = lastPart.indexOf('?');
    return queryIdx > 0 ? lastPart.substring(0, queryIdx) : lastPart;
  }
  return 'unknown';
}
